import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateAccessDeniedHeader,
    EmptyStateActions,
} from '@gitbook/spine-react';

import { useHideSplashscreen } from '../../Components';
import { useUserCustomClaims } from '../../state';
import { SSOOrganizationLogin } from './SSOOrganizationLogin';

/**
 * EnforceSSOWrapper is used to show an SSO login screen/access when the
 * currently logged in user doesn't have SSO linked and attempts to access org
 * owned content where the org has "Enforce SSO" enabled.
 *
 * If the related org has no "enforce SSO" enabled, or if the current user already
 * has already SSO linked to the org, we pass through and render the content.
 */
export function EnforceSSOWrapper(props: {
    organization: api.Organization | null;
    children: React.ReactNode;
}): React.ReactElement {
    useHideSplashscreen(0);
    const { organization } = props;
    const userCustomClaims = useUserCustomClaims();

    if (organization?.sso) {
        if (!userCustomClaims.sso) {
            // The current organization (parsed from navigation screen state)
            // has SSO enforced, but the currently logged in user doesn't have
            // SSO linked. Render SAML SSO login screen to allow them to "re
            // sign-in" using SSO.
            return (
                <SSOOrganizationLogin
                    organization={organization}
                    onUseNormalLogin={() => {}}
                    loggedIn
                />
            );
        }
        if (userCustomClaims.sso !== organization.id) {
            // User has SSO linked, but to a different organization.
            return (
                <EmptyState>
                    <EmptyStateAccessDeniedHeader />
                    <Description>
                        You don't have permissions to access this space. Contact the organization
                        administrator to configure access.
                    </Description>
                    <EmptyStateActions>
                        <Button
                            kind="secondary"
                            size="medium"
                            href={{ screen: { type: 'home' } }}
                            label="Take me home"
                        />
                    </EmptyStateActions>
                </EmptyState>
            );
        }
    }

    return <>{props.children}</>;
}
